import { z } from "zod";
import { PartnerSlugSchema } from "../integrations/inputs";

export const partnerConfigLifecycleStates = [
  "discovering",
  "active",
  "preferred",
  "migrating",
  "sunset",
] as const;

export const partnerConfigVerificationStrategies = [
  "hookdeck-native",
  "hookdeck-generic-hmac",
  "adapter-implements",
  "unverified-schema-only",
] as const;

export const PartnerConfigSchema = z.object({
  id: z.string().uuid().describe("ID"),
  account_id: z.string().uuid().describe("Account"),
  business_id: z.string().uuid().nullable().describe("Business"),
  partner_slug: PartnerSlugSchema,
  lifecycle_state: z
    .enum(partnerConfigLifecycleStates)
    .describe("Lifecycle State"),
  migration_phase: z.number().int().describe("Migration Phase"),
  credential_id: z.string().uuid().nullable().describe("Credential"),
  is_active: z.boolean().describe("Active"),
  sync_entities: z.array(z.string()).describe("Sync Entities"),
  sync_frequency: z.string().describe("Sync Frequency"),
  inbound_enabled: z.boolean().describe("Inbound Enabled"),
  outbound_enabled: z.boolean().describe("Outbound Enabled"),
  hookdeck_source_id: z.string().nullable().describe("Hookdeck Source ID"),
  hookdeck_source_type: z.string().nullable().describe("Hookdeck Source Type"),
  verification_strategy: z
    .enum(partnerConfigVerificationStrategies)
    .describe("Verification Strategy"),
  created_at: z.string().describe("Created At"),
  updated_at: z.string().describe("Updated At"),
});
export type PartnerConfig = z.infer<typeof PartnerConfigSchema>;

// `business_id` is intentionally omitted — it is injected from the
// authenticated tenant context server-side (see services/configs.ts). A
// client-supplied `business_id` is silently ignored.
export const PartnerConfigInsertSchema = z.object({
  partner_slug: PartnerSlugSchema,
  migration_phase: z
    .number()
    .int()
    .min(1)
    .max(4)
    .optional()
    .describe("Migration Phase"),
  credential_id: z.string().uuid().nullable().optional().describe("Credential"),
  is_active: z.boolean().optional().describe("Active"),
  sync_entities: z.array(z.string()).optional().describe("Sync Entities"),
  sync_frequency: z.string().optional().describe("Sync Frequency"),
  inbound_enabled: z.boolean().optional().describe("Inbound Enabled"),
  outbound_enabled: z.boolean().optional().describe("Outbound Enabled"),
  hookdeck_source_id: z
    .string()
    .nullable()
    .optional()
    .describe("Hookdeck Source ID"),
  hookdeck_source_type: z
    .string()
    .nullable()
    .optional()
    .describe("Hookdeck Source Type"),
  verification_strategy: z
    .enum(partnerConfigVerificationStrategies)
    .describe("Verification Strategy"),
});
export type PartnerConfigInsert = z.infer<typeof PartnerConfigInsertSchema>;

// PATCH excludes partner_slug (identity), lifecycle_state (M3 concern),
// credential_id (managed via OAuth flow), and verification_strategy (set at
// connection time only).
export const PartnerConfigUpdateSchema = z
  .object({
    migration_phase: z
      .number()
      .int()
      .min(1)
      .max(4)
      .optional()
      .describe("Migration Phase"),
    is_active: z.boolean().optional().describe("Active"),
    sync_entities: z.array(z.string()).optional().describe("Sync Entities"),
    sync_frequency: z.string().optional().describe("Sync Frequency"),
    inbound_enabled: z.boolean().optional().describe("Inbound Enabled"),
    outbound_enabled: z.boolean().optional().describe("Outbound Enabled"),
    hookdeck_source_id: z
      .string()
      .nullable()
      .optional()
      .describe("Hookdeck Source ID"),
    hookdeck_source_type: z
      .string()
      .nullable()
      .optional()
      .describe("Hookdeck Source Type"),
  })
  .strict();
export type PartnerConfigUpdate = z.infer<typeof PartnerConfigUpdateSchema>;

export const PartnerConfigStatusErrorSchema = z.object({
  received_at: z.string().describe("Received At"),
  error: z.string().nullable().describe("Error"),
  partner_event_id: z.string().nullable().describe("Partner Event ID"),
});
export type PartnerConfigStatusError = z.infer<
  typeof PartnerConfigStatusErrorSchema
>;

export const PartnerConfigStatusSchema = z.object({
  config_id: z.string().uuid().describe("Config"),
  last_event_at: z.string().nullable().describe("Last Event At"),
  event_count: z.number().int().describe("Event Count"),
  pending_count: z.number().int().describe("Pending Count"),
  failed_count: z.number().int().describe("Failed Count"),
  recent_errors: z
    .array(PartnerConfigStatusErrorSchema)
    .describe("Recent Errors"),
});
export type PartnerConfigStatus = z.infer<typeof PartnerConfigStatusSchema>;
